import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import {
  serviceCategoriesApi,
  serviceCategoryQueryKeys,
  type ServiceCategory,
} from "../../shared/api/serviceCategories";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * Trạng thái của một nhóm, tách khỏi cờ "Hiển thị trên App" (mô hình SalePro
 * /products/categories: `display` là Switch, `status` là dropdown). Ngưng hoạt động =
 * nhóm không còn chọn được khi thêm/sửa dịch vụ; ẩn trên App chỉ ảnh hưởng kênh bệnh nhân.
 */
export const CATEGORY_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Đang hoạt động", className: "text-emerald-700" },
  { value: "INACTIVE", label: "Ngưng hoạt động", className: "text-destructive" },
] as const;

const STATUS_SELECT_CLASS =
  "h-8 rounded-md border border-input bg-background px-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring";

/** Cài đặt → Danh mục → Nhóm dịch vụ (CRUD). */
export function ServiceCategoriesSettingsPage() {
  const qc = useQueryClient();
  const key = serviceCategoryQueryKeys.all();
  const q = useQuery({ queryKey: key, queryFn: () => serviceCategoriesApi.list(true) });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => serviceCategoriesApi.create({ name: name.trim() }),
    onSuccess: () => {
      setName("");
      setErr(null);
      invalidate();
    },
    onError: (e) => setErr(apiError(e).detail),
  });
  const update = useMutation({
    mutationFn: (v: { id: string; patch: Partial<ServiceCategory> }) =>
      serviceCategoriesApi.update(v.id, {
        name: v.patch.name ?? "",
        sortOrder: v.patch.sortOrder,
        active: v.patch.active,
        displayOnApp: v.patch.displayOnApp,
      }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => serviceCategoriesApi.remove(id),
    onSuccess: invalidate,
  });

  const rows = q.data ?? [];

  return (
    <div>
      <PageHeader
        title="Nhóm dịch vụ"
        description="Quản lý các nhóm để gom dịch vụ (Khám / Xét nghiệm / Chẩn đoán hình ảnh / …). Dùng khi thêm dịch vụ, lọc và lập hoá đơn."
      />

      <Card className="mb-4 max-w-lg">
        <CardContent className="p-4">
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim()) return;
              create.mutate();
            }}
          >
            <div className="grid flex-1 gap-1.5">
              <Label htmlFor="new-cat">Thêm nhóm mới</Label>
              <Input
                id="new-cat"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="VD: Nội soi"
                maxLength={128}
              />
            </div>
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              {create.isPending ? "Đang thêm…" : "Thêm"}
            </Button>
          </form>
          {err && <p className="mt-2 text-sm text-destructive">{err}</p>}
        </CardContent>
      </Card>

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">TT</TableHead>
              <TableHead>Tên nhóm</TableHead>
              <TableHead>Mã</TableHead>
              <TableHead className="w-40 text-center">Hiển thị trên App</TableHead>
              <TableHead className="w-44">Trạng thái</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  Đang tải…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  Chưa có nhóm dịch vụ.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((c) => (
                <TableRow key={c.id} className={c.active ? "" : "opacity-60"}>
                  <TableCell>
                    <Input
                      type="number"
                      defaultValue={c.sortOrder}
                      className="h-8 w-16"
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (v !== c.sortOrder)
                          update.mutate({ id: c.id, patch: { name: c.name, sortOrder: v } });
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      defaultValue={c.name}
                      className="h-8"
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v && v !== c.name)
                          update.mutate({ id: c.id, patch: { name: v } });
                      }}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{c.code}</TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={c.displayOnApp}
                      disabled={update.isPending}
                      aria-label={`Hiển thị trên App: ${c.name}`}
                      onCheckedChange={(value) =>
                        update.mutate({
                          id: c.id,
                          patch: { name: c.name, displayOnApp: value },
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <select
                      aria-label={`Trạng thái: ${c.name}`}
                      className={
                        STATUS_SELECT_CLASS +
                        " " +
                        (CATEGORY_STATUS_OPTIONS.find(
                          (o) => o.value === (c.active ? "ACTIVE" : "INACTIVE")
                        )?.className ?? "")
                      }
                      value={c.active ? "ACTIVE" : "INACTIVE"}
                      disabled={update.isPending}
                      onChange={(e) => {
                        const active = e.target.value === "ACTIVE";
                        if (active !== c.active)
                          update.mutate({ id: c.id, patch: { name: c.name, active } });
                      }}
                    >
                      {CATEGORY_STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value} className={o.className}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label="Xoá"
                      onClick={() => {
                        if (confirm(`Xoá nhóm "${c.name}"?`)) remove.mutate(c.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
